import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'hooks';
import { Icon } from 'shared/ui/icon/icon';
import { ICONS } from 'shared/constants/images';
import { Filter } from 'shared/constants/color-filters';
import { ProgressBarSegment } from 'shared/ui/progress-bar-segment/progress-bar-segment';
import { formatElapsed, formatExpected } from 'shared/helpers/reportTimeline';
import cls from './blueprint-placeholder.module.scss';

export type BlueprintUnitState = 'queued' | 'in-progress';

interface BlueprintPlaceholderProps {
  unitState: BlueprintUnitState;
  name: string;
  startedAt?: string | null;
  expectedSeconds?: number | null;
  className?: string;
}

export const BlueprintPlaceholder: FC<BlueprintPlaceholderProps> = ({
  unitState,
  name,
  startedAt = null,
  expectedSeconds = null,
  className = '',
}) => {
  const { t } = useTranslation();
  const isWorking = unitState === 'in-progress';
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!isWorking || !startedAt) {
      return;
    }
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isWorking, startedAt]);

  const elapsed =
    isWorking && startedAt
      ? formatElapsed((now - Date.parse(startedAt)) / 1000)
      : null;
  const expected =
    isWorking && expectedSeconds !== null
      ? formatExpected(expectedSeconds)
      : null;

  return (
    <div className={`${cls.blueprint} ${className}`} data-state={unitState}>
      <div className={cls.head}>
        <p className={`${cls.name} typescale-body-medium`}>{name}</p>
        <span className={`${cls.status} typescale-body-small`}>
          {isWorking ? (
            t('analyzing')
          ) : (
            <>
              <Icon
                pathToIcon={ICONS.clock}
                size={16}
                filter={Filter.onSurfaceVariant}
              />
              {t('inQueue')}
            </>
          )}
          {elapsed !== null && (
            <>
              <span className={cls.separator} aria-hidden='true'>
                ·
              </span>
              <span className={cls.meter}>
                {expected !== null
                  ? t('elapsedOfExpected', undefined, undefined, {
                      elapsed,
                      expected,
                    })
                  : elapsed}
              </span>
            </>
          )}
        </span>
      </div>

      <div className={cls.bar}>
        <ProgressBarSegment progress={0} isActive={isWorking} />
      </div>
    </div>
  );
};
